import { useEffect, useLayoutEffect, useRef } from 'react';

const AXIS_RATIO = 1.2;
const SLOP = 10;
const FLICK_SPEED = 0.65;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const reducedMotion = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
const interactive = target => target?.closest?.('button, a, input, textarea, select, [contenteditable="true"], canvas');
const axisFor = (x, y) => Math.abs(x) > Math.abs(y) * AXIS_RATIO ? 'x'
  : Math.abs(y) > Math.abs(x) * AXIS_RATIO ? 'y' : null;

// Only transforms change during a drag. React and learning callbacks run on release,
// not on every touch sample. The parent owns the accepted navigation's exit/entry.
export default function useCardSwipeGesture({ identity, disabled, canSwipe, onHorizontal, onVertical, maxRotation = 75 }) {
  const surfaceRef = useRef(null);
  const gesture = useRef(null);
  const settlement = useRef(null);
  const timer = useRef(null);
  const motion = useRef({ x: 0, y: 0, rotation: 0 });
  const suppressUntil = useRef(0);
  const latest = useRef(null);
  latest.current = { disabled, canSwipe, onHorizontal, onVertical, maxRotation };

  const draw = (next, phase) => {
    motion.current = next;
    const surface = surfaceRef.current;
    if (!surface) return;
    surface.dataset.swipePhase = phase;
    surface.style.setProperty('--flashcard-drag-x', `${next.x}px`);
    surface.style.setProperty('--flashcard-drag-y', `${next.y}px`);
    surface.style.setProperty('--flashcard-drag-rotation', `${next.rotation}deg`);
  };
  const releasePointer = current => {
    if (current?.source !== 'pointer') return;
    const surface = surfaceRef.current;
    if (surface?.hasPointerCapture?.(current.id)) surface.releasePointerCapture(current.id);
  };
  const reset = () => {
    const current = gesture.current;
    gesture.current = null;
    settlement.current = null;
    clearTimeout(timer.current);
    releasePointer(current);
    draw({ x: 0, y: 0, rotation: 0 }, 'idle');
  };
  const cancel = () => {
    if (!gesture.current) return;
    suppressUntil.current = Date.now() + 700;
    reset();
  };
  const resetRef = useRef(reset);
  resetRef.current = reset;
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;
  useLayoutEffect(() => { resetRef.current(); suppressUntil.current = 0; }, [identity]);
  useEffect(() => { if (disabled) cancelRef.current(); }, [disabled]);
  useEffect(() => {
    const blur = () => cancelRef.current();
    const hidden = () => { if (document.hidden) blur(); };
    const anotherPointer = event => {
      if (gesture.current?.source === 'pointer' && gesture.current.id !== event.pointerId) blur();
    };
    const multipleTouches = event => { if (event.touches?.length > 1) blur(); };
    window.addEventListener('blur', blur);
    window.addEventListener('pointerdown', anotherPointer, true);
    window.addEventListener('touchstart', multipleTouches, true);
    document.addEventListener('visibilitychange', hidden);
    return () => {
      window.removeEventListener('blur', blur);
      window.removeEventListener('pointerdown', anotherPointer, true);
      window.removeEventListener('touchstart', multipleTouches, true);
      document.removeEventListener('visibilitychange', hidden);
      clearTimeout(timer.current);
      const current = gesture.current;
      gesture.current = null;
      settlement.current = null;
      releasePointer(current);
    };
  }, []);

  const begin = (event, point, source) => {
    if (gesture.current) { cancel(); return; }
    if (!point || latest.current.disabled || settlement.current) return;
    suppressUntil.current = 0;
    if (interactive(event.target)) return;
    const bounds = surfaceRef.current.getBoundingClientRect();
    const now = performance.now();
    gesture.current = { source, id: source === 'touch' ? point.identifier : event.pointerId,
      start: { x: point.clientX, y: point.clientY }, last: { x: point.clientX, y: point.clientY },
      samples: [{ x: point.clientX, y: point.clientY, time: now }], axis: null,
      width: bounds.width || 450, height: bounds.height || 320,
      scroll: event.target?.closest?.('.flashcard-content'), scrollTop: event.target?.closest?.('.flashcard-content')?.scrollTop || 0 };
    if (source === 'pointer') surfaceRef.current.setPointerCapture?.(event.pointerId);
  };
  const update = point => {
    const current = gesture.current;
    if (!current || !point || latest.current.disabled) { cancel(); return null; }
    const x = point.clientX - current.start.x;
    const y = point.clientY - current.start.y;
    const now = performance.now();
    current.last = { x: point.clientX, y: point.clientY };
    current.samples = [...current.samples.filter(sample => now - sample.time <= 100), { ...current.last, time: now }];
    if (!current.axis && Math.max(Math.abs(x), Math.abs(y)) >= SLOP) {
      current.axis = axisFor(x, y);
      const scroll = current.scroll;
      if (current.axis === 'y' && scroll?.scrollHeight > scroll?.clientHeight + 2 &&
        ((y > 0 && current.scrollTop > 0) || (y < 0 && current.scrollTop < scroll.scrollHeight - scroll.clientHeight))) current.axis = 'scroll';
    }
    if (current.axis === 'scroll') {
      current.scroll.scrollTop = clamp(current.scrollTop - y, 0, current.scroll.scrollHeight - current.scroll.clientHeight);
      return current;
    }
    const direction = current.axis === 'x' ? (x < 0 ? 'left' : 'right') : (y < 0 ? 'up' : 'down');
    if (!current.axis || !latest.current.canSwipe(direction)) {
      draw({ x: 0, y: 0, rotation: 0 }, 'idle');
      return current;
    }
    const reduced = reducedMotion();
    draw(current.axis === 'x'
      ? { x: clamp(x * 0.12, -18, 18), y: 0, rotation: reduced ? 0 : clamp(x / current.width * 180, -latest.current.maxRotation, latest.current.maxRotation) }
      : { x: 0, y: reduced ? clamp(y * 0.1, -12, 12) : clamp(y * 0.8, -current.height * 0.65, current.height * 0.65), rotation: 0 }, 'dragging');
    return current;
  };
  const end = point => {
    const current = gesture.current;
    if (!current || latest.current.disabled) { cancel(); return; }
    const last = point || { clientX: current.last.x, clientY: current.last.y };
    update(last);
    if (!gesture.current) return;
    const x = last.clientX - current.start.x;
    const y = last.clientY - current.start.y;
    const distance = Math.max(Math.abs(x), Math.abs(y));
    if (distance >= SLOP || current.axis === 'scroll') suppressUntil.current = Date.now() + 700;
    const axis = current.axis;
    const amount = axis === 'x' ? x : y;
    const direction = axis === 'x' ? (x < 0 ? 'left' : 'right') : (y < 0 ? 'up' : 'down');
    const threshold = axis === 'x' ? clamp(current.width * 0.24, 80, 110) : clamp(current.height * 0.18, 60, 110);
    const recent = current.samples[0];
    const elapsed = performance.now() - recent.time;
    const travel = axis === 'x' ? last.clientX - recent.x : last.clientY - recent.y;
    // Require a deliberate minimum travel, a recent velocity sample and matching
    // release direction; a slow short drag or a pause before release is not a fling.
    const flick = elapsed >= 16 && elapsed <= 100 && Math.abs(amount) >= 28 &&
      Math.sign(travel) === Math.sign(amount) && Math.abs(travel / elapsed) >= FLICK_SPEED;
    if (!['x', 'y'].includes(axis) || !latest.current.canSwipe(direction) || (Math.abs(amount) < threshold && !flick)) { reset(); return; }
    const departure = { ...motion.current };
    const token = {};
    gesture.current = null;
    releasePointer(current);
    settlement.current = token;
    draw(axis === 'x' ? { x: 0, y: 0, rotation: 0 } : departure, 'settling');
    const restore = () => { if (settlement.current === token) reset(); };
    // A fallback also releases a blocked callback or a parent waiting on a popup.
    timer.current = setTimeout(restore, axis === 'x' ? 240 : 1600);
    try {
      const result = axis === 'x' ? latest.current.onHorizontal(direction, departure) : latest.current.onVertical(direction);
      if (result === false) restore();
      else if (result?.then) Promise.resolve(result).then(restore, restore);
    } catch { restore(); }
  };
  const touches = event => event.touches?.length ? event.touches : event.targetTouches;
  const trackedTouch = list => Array.from(list || []).find(point => point.identifier === gesture.current?.id);
  return {
    surfaceRef,
    isSettling: () => Boolean(settlement.current),
    suppressClick: event => event.detail > 0 && Date.now() < suppressUntil.current,
    reset,
    cancel,
    handlers: {
      onTouchStart: event => { const list = touches(event); if (list?.length !== 1) { cancel(); return; } if (gesture.current?.source === 'pointer') return; begin(event, list[0], 'touch'); },
      onTouchMove: event => { if (gesture.current?.source !== 'touch') return; const list = touches(event); if (list?.length !== 1) { cancel(); return; } update(trackedTouch(list)); },
      onTouchEnd: event => { if (gesture.current?.source !== 'touch') return; const point = trackedTouch(event.changedTouches); if (event.changedTouches?.length && !point) return; end(point); },
      onTouchCancel: cancel,
      onPointerDown: event => { if (event.button !== 0 || event.isPrimary === false) return; begin(event, event, 'pointer'); },
      onPointerMove: event => { if (gesture.current?.source === 'pointer' && gesture.current.id === event.pointerId) update(event); },
      onPointerUp: event => { if (gesture.current?.source === 'pointer' && gesture.current.id === event.pointerId) end(event); },
      onPointerCancel: event => { if (gesture.current?.source === 'pointer' && gesture.current.id === event.pointerId) cancel(); },
      onLostPointerCapture: event => { if (gesture.current?.source === 'pointer' && gesture.current.id === event.pointerId) cancel(); },
    },
  };
}
